import axios from "axios";
import FalconLightBox from "components/common/FalconLightBox";
import Flex from "components/common/Flex";
import QuillEditor from "components/common/QuillEditor";
import SubtleBadge from "components/common/SubtleBadge";
import JoditTextEditor from "components/examples/JoditEditor/JoditTextEditor";
import SunTextEditor from "components/examples/JoditEditor/JoditTextEditor";
import ImageCropperModal from "components/image_crop/ImageCropperModal";
import { useFetchCategoriesGroup } from "hooks/useFetchCategoriesGroup";
import { useFetchPageGroups } from "hooks/useFetchPagesGroup";
import JoditEditor from "jodit-react";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Card, Col, Form, Image, Row } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { FaImage } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { BASEURL, customSettings } from "utils/customConfig";
import CustomOption from "utils/CustomOption";

const PageBasicInformation = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const {
    register,
    setValue,
    control,
    watch,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext();
  const [tags, setTags] = useState(getValues("tags") || []);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const editor = useRef(null);

  const titleWatch = watch("title");
  const authorWatch = watch("author");
  const linkWatch = watch("link");
  const maxCharactersLength = 255;

  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [tempImagePreview, setTempImagePreview] = useState("");

  const { data: pageGroupsData, isLoading: pageGroupsLoading } =
    useFetchPageGroups();

  const CATEGORY_BANNER_SIZE = window.imageConfig
    ?.CATEGORY_GROUP_BANNER_SIZE || {
    width: 1920,
    height: 1080,
  };

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept: {
      "image/*": [".jpeg", ".png", ".jpg"],
    },
    // onDrop: async (acceptedFiles) => {
    //   const uploadedFile = acceptedFiles[0];
    //   const uploadedFileUrl = await uploadFile(uploadedFile);

    //   setValue("backgroundImage", uploadedFileUrl);

    //   setFile(
    //     Object.assign(uploadedFile, {
    //       preview: URL.createObjectURL(uploadedFile),
    //     })
    //   );
    // },

    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setTempImageFile(file);
      setTempImagePreview(URL.createObjectURL(file));
      setShowCropModal(true);
    },
  });

  const handleCropResult = async (croppedFile) => {
    if (!croppedFile) return;

    setShowCropModal(false);

    const uploadedUrl = await uploadFile(croppedFile);

    setValue("backgroundImage", uploadedUrl);

    setFile(
      Object.assign(croppedFile, {
        preview: URL.createObjectURL(croppedFile),
      }),
    );
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && inputValue) {
      event.preventDefault();
      if (!tags?.includes(inputValue?.trim())) {
        setTags([...tags, inputValue?.trim()]);
      }
      setInputValue("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags?.filter((tag) => tag !== tagToRemove));
  };

  useEffect(() => {
    // Register fields with react-hook-form
    register("contentDetail");
  }, [register]);

  useEffect(() => {
    if (state) {
      // console.log(state);
      setTags(state?.tagsList || []);
      setValue("contentDetail", state.contentDetail || "");
    }
  }, [state, setValue]);

  useEffect(() => {
    // Update form value for tags when local state changes
    setValue("tags", tags);
    let date = state?.postDate
      ? new Date(state?.postDate).toISOString().split("T")[0]
      : "";
    setValue("postDate", date);
  }, [tags, setValue]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);
      const response = await axios.post("/Catalogue/CategoryFile", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setLoading(false);

      // Assuming the response contains the URL of the uploaded file
      return response.data[0].destination;
    } catch (error) {
      console.error("File upload failed:", error);
      toast.error("Failed to File Upload", {
        position: customSettings.toastPosition,
        autoClose: customSettings.toastClose,
      });
      return "";
    }
  };

  useEffect(() => {
    register("pageGroupId", { required: true });
  }, [register]);

  useEffect(() => {
    if (state) {
      setValue("pageGroupId", state.categoryId);
    }
  }, [state, setValue]);

  const handleSelectChange = (selectedOption) => {
    setValue("pageGroupId", selectedOption ? selectedOption.value : "");
    if (selectedOption) {
      clearErrors("pageGroupId");
    }
  };

  const handleInputFocus = () => {
    clearErrors("pageGroupId");
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      cursor: "pointer",
      borderColor: errors.pageGroupId ? "red" : provided.borderColor,
      "&:hover": {
        borderColor: errors.pageGroupId
          ? "red"
          : provided["&:hover"].borderColor,
      },
    }),
    input: (provided) => ({
      ...provided,
      color: "black",
      paddingTop: "4px",
      paddingBottom: "4px",
      // padding: "12px", // Set the input text color to black
    }),

    menu: (provided) => ({
      ...provided,
      height: 170,
      color: "black",
      zIndex: 1, // High enough to ensure visibility
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      height: 170,
      zIndex: 1, // High enough to ensure visibility
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1,
    }),
  };

  const flattenOptions = (nodes) => {
    return nodes?.reduce((acc, node) => {
      acc.push({ value: node.id, label: node.name });
      return acc;
    }, []);
  };

  const flatOptions = flattenOptions(pageGroupsData?.data?.data);

  let initialSelectedOption = flatOptions?.find(
    (option) => option.value === state?.pageGroupId,
  );

  // console.log(state);

  if (initialSelectedOption == undefined) {
    initialSelectedOption = state
      ? { label: state?.pageGroupId, value: state?.pageGroupId }
      : undefined;
  }

  const editorRef = useRef(null);

  // ✅ Use state to track content and prevent disabling
  const [content, setContent] = useState("");

  // ✅ Ensure onChange function remains stable
  const handleEditorChange = (value) => {
    setContent(value);
  };

  // console.log(watch("contentDetail"));

  return (
    <>
      {pageGroupsLoading ? (
        <div className="d-flex py-3 justify-content-center align-items-center">
          <Button variant="falcon-default" size="sm" className="fs-10">
            Loading...
          </Button>
        </div>
      ) : (
        <Card.Body
          className=""
          style={{
            height: `calc(100vh - 12.7rem)`,
            overflow: "auto",
            padding: "5px 20px",
          }}
        >
          <Row className="gx-2 pb-2">
            <Col md="6">
              <Form.Group>
                {/* <FloatingLabel controlId="floatingInput" label="Name"> */}
                <Form.Control
                  type="text"
                  placeholder="Title"
                  title="Title"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.title}
                  {...register("title")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.title?.message}
                  </Form.Control.Feedback>
                  <small className="fs-10 text-muted">
                    {`${titleWatch?.length || 0} / ${maxCharactersLength}`}
                  </small>
                </div>
                {/* <Form.Control.Feedback type="invalid">
                  {errors.title?.message}
                </Form.Control.Feedback> */}
                {/* </FloatingLabel> */}
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                {/* <Form.Label>First name:</Form.Label> */}
                <div title="Select a Page Group">
                  <Select
                    options={flatOptions}
                    components={{ Option: CustomOption }}
                    isClearable
                    placeholder={
                      <h6 className="mb-2 mb-md-0">
                        Page Group{" "}
                        {/* <span style={{ color: "red", fontSize: "12px" }}>*</span> */}
                      </h6>
                    }
                    className={`${errors.pageGroupId ? "is-invalid" : ""}`}
                    styles={customStyles}
                    onChange={handleSelectChange}
                    onFocus={handleInputFocus}
                    onInputChange={handleInputFocus}
                    defaultValue={initialSelectedOption}
                  />
                </div>
                <Form.Control.Feedback type="is-invalid">
                  <span className="fs-10" style={{ color: "#e63757" }}>
                    {errors?.pageGroupId?.message}
                  </span>
                </Form.Control.Feedback>
              </Form.Group>
              {/* <Form.Group>
                <Form.Select
                  aria-label="CategoryGroup"
                  type="text"
                  className="mb-1 pb-2"
                  title="Category Group"
                  isInvalid={!!errors?.pageGroupId}
                  {...register("pageGroupId")}
                >
                  <option value="">Select Category Group</option>
                  {categoriesGroupData?.data?.data.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.pageGroupId?.message}
                </Form.Control.Feedback>
              </Form.Group> */}
            </Col>
            <Col md="6">
              <Form.Group>
                {/* <FloatingLabel controlId="floatingInput" label="Name"> */}
                <Form.Control
                  type="text"
                  placeholder="Author"
                  title="Author"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.author}
                  {...register("author")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.author?.message}
                  </Form.Control.Feedback>
                  <small className="fs-10 text-muted">
                    {`${authorWatch?.length || 0} / ${maxCharactersLength}`}
                  </small>
                </div>
                {/* <Form.Control.Feedback type="invalid">
                  {errors.author?.message}
                </Form.Control.Feedback> */}
                {/* </FloatingLabel> */}
              </Form.Group>
            </Col>

            <Col md="6">
              <Form.Group>
                {/* <FloatingLabel controlId="floatingInput" label="Name"> */}
                <Form.Control
                  type="number"
                  placeholder="Display Order"
                  title="Display Order"
                  className="py-2 mb-1"
                  isInvalid={!!errors.sortOrder}
                  {...register("sortOrder")}
                  onChange={(e) => {
                    // clearErrors("sortOrder");
                    const value = e.target.value;
                    // Allow only positive integers or empty
                    if (value === "" || /^[0-9]\d*$/.test(value)) {
                      e.target.value = value; // Set the value if it's valid
                    } else {
                      e.target.value = ""; // Reset if invalid
                    }
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.sortOrder?.message}
                </Form.Control.Feedback>
                {/* </FloatingLabel> */}
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                {/* <FloatingLabel controlId="floatingInput" label="Name"> */}
                <Form.Control
                  type="text"
                  placeholder="Link"
                  title="Link"
                  maxLength={maxCharactersLength}
                  className="py-2"
                  isInvalid={!!errors.link}
                  {...register("link")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.link?.message}
                  </Form.Control.Feedback>
                  <small className="fs-10 text-muted">
                    {`${linkWatch?.length || 0} / ${maxCharactersLength}`}
                  </small>
                </div>

                {/* </FloatingLabel> */}
              </Form.Group>
            </Col>
            <Col md="3">
              <Form.Group>
                {/* <FloatingLabel controlId="floatingInput" label="Name"> */}
                <Form.Control
                  type="date"
                  placeholder="Date"
                  title="Date"
                  className="py-2 mb-1"
                  isInvalid={!!errors.postDate}
                  // value={moment.utc(state?.postDate).format("YYYY-MM-DD")}
                  // defaultValue={state?.postDate.split("T")[0]}
                  {...register("postDate")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.postDate?.message}
                </Form.Control.Feedback>
                {/* </FloatingLabel> */}
              </Form.Group>
            </Col>
            <Col md="3">
              <Form.Check
                type="checkbox"
                id="defaultSwitch"
                label="Date showable to Customer"
                className="mt-2"
                defaultChecked={!state}
                isInvalid={!!errors.showDate}
                {...register("showDate")}
              />
            </Col>
            <Col md="6">
              <div className="form-control d-flex flex-wrap align-items-center mb-1">
                {tags?.map((tag, index) => (
                  <SubtleBadge key={index} className=" mr-2 mb-1 mx-1">
                    {tag}
                    <span
                      className="ml-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleRemoveTag(tag)}
                    >
                      &times;
                    </span>
                  </SubtleBadge>
                ))}
                <input
                  type="text"
                  title="Tags"
                  className="border-0 flex-grow-1"
                  style={{
                    outline: "none",
                    color: "black",
                    minWidth: "120px",
                    // paddingBottom: "10px",
                    // paddingTop: "10px",
                  }}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Tags"
                />
              </div>
            </Col>
            <Col md="6">
              <div className="d-flex justify-content-between">
                <div className="d-flex gap-3">
                  <div {...getRootProps()}>
                    <input {...getInputProps({ multiple: false })} />
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      // className="px-4"
                    >
                      <FaImage
                        size={27}
                        title="Image"
                        className="mt-2 cursor-pointer mr-1"
                      />
                      <span
                        style={{
                          fontSize: "10px",
                          color: "red",
                          marginLeft: 4,
                          marginTop: 8,
                        }}
                      >
                        ({CATEGORY_BANNER_SIZE.width}px ×{" "}
                        {CATEGORY_BANNER_SIZE.height}px)
                      </span>
                      {/* <p className="fs-9 mb-0 text-700"> Select Logo</p> */}
                    </Flex>
                  </div>

                  {/* <Col md={file || (state?.logoFile?.fileBase64 ? "2" : "1")}> */}
                  {file || state?.backgroundImageRelativeUrl ? (
                    <FalconLightBox
                      image={
                        file
                          ? file.preview
                          : `${BASEURL}${state.backgroundImageRelativeUrl}`
                      }
                    >
                      <Image
                        src={
                          file
                            ? file.preview
                            : `${BASEURL}${state.backgroundImageRelativeUrl}`
                        }
                        rounded
                        width={80}
                        height={40}
                      />
                    </FalconLightBox>
                  ) : null}
                  {/* Render nothing if both file and state.logoFile.fileBase64 are not present */}
                </div>
                <div className="d-flex gap-2">
                  <Form.Check
                    type="checkbox"
                    id="defaultSwitch"
                    label="Is Published"
                    className="mt-2"
                    defaultChecked={!state}
                    isInvalid={!!errors.isPublished}
                    {...register("isPublished")}
                  />
                  <Form.Check
                    type="checkbox"
                    id="defaultSwitch"
                    label="Active"
                    className="mt-2"
                    defaultChecked={!state}
                    isInvalid={!!errors.status}
                    {...register("status")}
                  />
                </div>
              </div>
            </Col>

            {/* <Col md={12}>
              <QuillEditor
                placeholder="Content"
                setValue={(value) => setValue("contentDetail", value)} // Sync with react-hook-form
                value={watch("contentDetail")} // Get the value from react-hook-form
              />
            </Col> */}

            <Col md={12}>
              <JoditEditor
                config={{
                  showCharsCounter: false,
                  showWordsCounter: false,
                  showXPathInStatusbar: false, // ✅ Removes footer bar
                  buttons: [
                    "source",
                    "bold",
                    "italic",
                    "underline",
                    "strikethrough",
                    "superscript",
                    "subscript",
                    "ul",
                    "ol",
                    "indent",
                    "font",
                    "fontsize",
                    "brush",
                    "paragraph",
                    "align",
                    "hr",
                    "eraser",
                    "copyformat",
                    "symbol",
                    "fullsize",
                    "print",
                    "about",
                    "selectall",
                    // "copy",
                    "paste",
                    "link",
                    "unlink",
                    "image",
                    "file",
                    "video",
                    "table",
                    "preview",
                    "dots", // Dots button expands extra options
                  ],
                  placeholder: "Short Description",
                  toolbarAdaptive: false, // Show full toolbar without collapsing
                  toolbarSticky: false, // Keep toolbar fixed when scrolling
                  uploader: {
                    insertImageAsBase64URI: true, // ✅ Allows adding Base64 images directly
                  },
                }}
                value={watch("shortDescription")} // ✅ Watch ensures form binding
                onBlur={(value) => setValue("shortDescription", value)} // preferred to use only this option to update the content
              />
            </Col>
            <Col md={12} className="mt-2">
              <JoditEditor
                config={{
                  showCharsCounter: false,
                  showWordsCounter: false,
                  showXPathInStatusbar: false, // ✅ Removes footer bar
                  buttons: [
                    "source",
                    "bold",
                    "italic",
                    "underline",
                    "strikethrough",
                    "superscript",
                    "subscript",
                    "ul",
                    "ol",
                    "indent",
                    "font",
                    "fontsize",
                    "brush",
                    "paragraph",
                    "align",
                    "hr",
                    "eraser",
                    "copyformat",
                    "symbol",
                    "fullsize",
                    "print",
                    "about",
                    "selectall",
                    // "copy",
                    "paste",
                    "link",
                    "unlink",
                    "image",
                    "file",
                    "video",
                    "table",
                    "preview",
                    "dots", // Dots button expands extra options
                  ],
                  placeholder: "Description",
                  toolbarAdaptive: false, // Show full toolbar without collapsing
                  toolbarSticky: false, // Keep toolbar fixed when scrolling
                  uploader: {
                    insertImageAsBase64URI: true, // ✅ Allows adding Base64 images directly
                  },
                }}
                value={watch("contentDetail")} // ✅ Watch ensures form binding
                onBlur={(value) => setValue("contentDetail", value)} // preferred to use only this option to update the content
              />
            </Col>

            <Col md={12}>
              <Form.Group className="my-2">
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Meta Description"
                  {...register("metaDescription")}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Meta Keywords"
                  {...register("metaKeywords")}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      )}
      <ImageCropperModal
        show={showCropModal}
        imageUrl={tempImagePreview}
        type="category-banner" // same type you used before (good for sizing)
        onClose={() => setShowCropModal(false)}
        onCropped={handleCropResult}
      />
    </>
  );
};

export default React.memo(PageBasicInformation);
